/**
 * Patches window.fetch once so every mutating request (form submits/fetch calls
 * RR7 makes for actions) carries the already-computed device fingerprint as a
 * header, so the server-side activity-log middleware can read it generically
 * without every form needing a `deviceFingerPrint` field.
 */
import type store from "~/store";

const MUTATING_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

let patched = false;

export function attachFingerprintHeader(appStore: typeof store) {
  if (patched || typeof window === "undefined") return;
  patched = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = (input: RequestInfo | URL, init: RequestInit = {}) => {
    const method = (init.method || (input instanceof Request ? input.method : "GET")).toUpperCase();
    if (!MUTATING_METHODS.has(method)) {
      return originalFetch(input, init);
    }

    // Same-origin only — this app's own actions, never a cross-origin call
    // like a presigned S3/R2 upload PUT (chat-support file attachments),
    // which can reject unexpected headers or simply shouldn't receive an
    // internal tracking identifier meant for backoffice request logging.
    const url = input instanceof Request ? input.url : input.toString();
    let isSameOrigin = false;
    try {
      isSameOrigin = new URL(url, window.location.origin).origin === window.location.origin;
    } catch {
      isSameOrigin = false;
    }
    if (!isSameOrigin) {
      return originalFetch(input, init);
    }

    const fingerprint = appStore.getState().global.deviceFingerPrint;
    if (!fingerprint) {
      return originalFetch(input, init);
    }

    const headers = new Headers(init.headers ?? (input instanceof Request ? input.headers : undefined));
    headers.set("X-Device-Fingerprint", fingerprint);

    return originalFetch(input, { ...init, headers });
  };
}
